import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';
import moment from 'moment';

interface IngredientsManagerFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;
    providers: any[];
    del_providers: any[];
    form: any;
    schema: any;

    meta: any;
}

const state: IngredientsManagerFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,
    providers: [],
    del_providers: [],
    form: null,
    schema: null,

    meta: {},
};

const initState: IngredientsManagerFormState = cloneDeep(state);

export const INGREDIENTS_MANAGER_DATA = 'GET_INGREDIENTS_MANAGER_DATA';
export const INGREDIENTS_MANAGER_SCHEMA = 'GET_INGREDIENTS_MANAGER_SCHEMA';
export const INGREDIENTS_PROVIDERS_DATA = 'GET_INGREDIENTS_PROVIDERS_DATA';

export const actions: ActionTree<IngredientsManagerFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        return dispatch('loadData', params);
    },

    loadData({ commit }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(INGREDIENTS_MANAGER_DATA)
                    .raw('documents.get')
                    .action({
                        id: params.id
                    }),
                vm.$q(INGREDIENTS_PROVIDERS_DATA)
                    .raw('documents.list')
                    .action({
                        "columns": {
                            "id": "vip.id",
                            "product_id": "p.id",
                            "is_primary": "vip.is_primary",
                            "label": "p.name",
                        },
                        "from": "s:voc_providers",
                        "from_as": "p",
                        "joins": [
                            {
                                "join": "s:voc_ingredients_providers",
                                // "type": '',
                                "as": "vip",
                                "on": {
                                    $and: [
                                        {"$eq": ['vip.provider_id', 'p.id']},
                                        {"$eq": ['vip.ingredient_id', {$: params.id}]}
                                    ]
                                }
                            }
                        ],
                        "page_size": 1000,
                    }),
            ]).then((result: ServerResult) => {
                commit('SET_FORM_DATA', {
                    document: result.getOperation(INGREDIENTS_MANAGER_DATA).getContent().document,
                    schema: result.getOperation(INGREDIENTS_MANAGER_DATA).getContent().schema,
                    providers: result.getOperation(INGREDIENTS_PROVIDERS_DATA).getContent().documents
                });

                commit('LOADED_SUCCESS');
                resolve(null);
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    prepareNew({ commit, state }, initParams) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(INGREDIENTS_MANAGER_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:voc_ingredients`
                    }),
            ]).then((result: ServerResult) => {
                commit('GENERATE_FORM_DATA', {
                    ...result.getOperation(INGREDIENTS_MANAGER_SCHEMA).getContent(),
                    initParams
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    saveForm({ commit }, documentId) {
        const vm = (this as any)._vm;

        const normalizedForm = prepareFormObjectForSave(state.schema, state.form);

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            let saveQuery = null;

            if (documentId) {
                saveQuery = vm.$q('INGREDIENTS_MANAGER_DATA_SAVE')
                    .update(documentId, normalizedForm)
                    .createManyRelation('voc_ingredients_providers', 'ingredient_id', (createRelated: any) => {
                        return state.providers.filter((item: any) => !item.id || (typeof(item.id) !== 'string')).map((item, index) => createRelated({provider_id: item.provider_id}));
                    })
                    .updateManyRelation('voc_ingredients_providers', 'ingredient_id', (updateRelated: any) => {
                        return state.providers.filter((item: any) => (typeof(item.id) == 'string')).map((item, index) => updateRelated({id: item.id, is_primary: item.is_primary}))
                    })
                    .deleteManyRelation('voc_ingredients_providers', 'ingredient_id', (deleteRelated: any) => {
                      return state.del_providers.map((item: any, index) => deleteRelated({ id: item.id }));
                    })
            } else {
                saveQuery = vm.$q('INGREDIENTS_MANAGER_DATA_SAVE')
                    .create('s:voc_ingredients', normalizedForm)
                    .withManyRelation('voc_ingredients_providers', 'ingredient_id', (createRelated: any) => {
                        return state.providers.map((item: any) => {
                            return createRelated({provider_id: item.provider_id});
                        });
                    })
            }

            vm.$db([
                saveQuery
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('INGREDIENTS_MANAGER_DATA_SAVE');

                if (operationResult.hasError()) {
                    console.log('error', operationResult.getContent());

                    commit('LOADED_SUCCESS');
                    reject();
                } else {
                    commit('ON_FORM_SAVE', operationResult.getContent())

                    commit('LOADED_SUCCESS');
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.error('errror', error)
                reject(error);
            });
        })
    },

    changeFormValue({commit}, {field, newValue}) {
        commit('CHANGE_VALUE', {field, newValue});
    },
    changeProviderValue({commit}, {idx, field, newValue}) {
        commit('CHANGE_PROVIDER_VALUE', {idx, field, newValue});
    },

    setArchive({state, rootState, commit}) {
        return new Promise((resolve, reject) => {
            commit('START_LOADING');
            const vm = (this as any)._vm;

            const normalizedForm = prepareFormObjectForSave(state.schema, {
                archived_at: moment().toISOString(),
                archived_user_id: (rootState as any).ubic.actor.document.id
            });
            console.log(normalizedForm)

            const saveQuery = vm.$q('SAVE')
                .update(state.form.id, normalizedForm)
            vm.$db([
                saveQuery
            ]).then(((result: ServerResult) => {
                console.log(result)
                commit('LOADED_SUCCESS');
                resolve();
            }));
        })
    },

    setUnarchive({state, commit}) {
        return new Promise((resolve, reject) => {
            commit('START_LOADING');
            const vm = (this as any)._vm;

            const normalizedForm = prepareFormObjectForSave(state.schema, {
                archived_at: null,
                archived_user_id: null
            });
            normalizedForm['archived_at'] = null;

            const saveQuery = vm.$q('SAVE')
                .update(state.form.id, normalizedForm)
            vm.$db([
                saveQuery
            ]).then(((result: ServerResult) => {
                console.log(result)
                commit('LOADED_SUCCESS');
                resolve();
            }));
        })
    },
    addProvider({commit}, {provider}) {
        if (provider) {
            commit('ADD_PROVIDER', {provider});
        }
    },
    delProvider({commit}, {provider}) {
        if (provider) {
            commit('DEL_PROVIDER', {provider});
        }
    },

};

export const mutations: MutationTree<IngredientsManagerFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_INGREDIENTS_MANAGER_DATA(state, {document}) {
        state.document = document;
    },

    SET_FORM_DATA(state, {document, schema, providers}) {
        state.document = document;
        state.providers = providers;
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, state.document);
    },

    ADD_PROVIDER(state, {provider}) {
        state.providers.push({
            provider_id: provider.id,
            label: provider.label,
        });
    },

    DEL_PROVIDER(state, {provider}) {
        state.providers.splice(state.providers.indexOf(provider), 1);
        state.del_providers.push(provider);
    },

    GENERATE_FORM_DATA(state, {schema, initParams}) {
        state.document = {};
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, initParams);
    },

    ON_FORM_SAVE(state, {document, schema}) {
        state.document = document;
        state.schema = schema;

        state.form = generateFormObject(schema, state.document);
    },

    CHANGE_VALUE(state: IngredientsManagerFormState, {field, newValue}) {
        switch (field) {
            default:
                state.form[field] = newValue;
        }
    },

    CHANGE_PROVIDER_VALUE(state: IngredientsManagerFormState, {idx, field, newValue}) {
        switch (field) {
            case 'is_primary':
                state.providers.map((item: any, index: number) => {
                    console.log(item)
                    if (index !== idx){
                        item.is_primary = false
                    }
                })
                break;

            default:
                state.providers[idx].form[field] = newValue;
        }
    },
};

const getters = {
    fields: (state: IngredientsManagerFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_INGREDIENTS_MANAGER_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
